import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { parse, format, isValid } from "date-fns";
import { colors } from "../../../styles/colors";
import InfoTooltip from "../overlays/InfoTooltip";
import { floatingLabelMaskSx } from "../../../styles/theme";

type Variant = "side-label" | "floating";

interface InputFieldDateProps {
  label?: string;
  /** Datumswert als ISO-String (yyyy-MM-dd) oder leer */
  value: string;
  /** Callback mit ISO-String (yyyy-MM-dd) oder null bei Löschung */
  onChange: (value: string | null) => void;
  /**
   * Abschluss der Eingabe – beim Verlassen des Feldes und bei der Auswahl im Kalender, mit dem
   * eingegebenen Datum bzw. null, wenn das Feld geleert wurde. Zum Speichern ohne Knopf
   * (ADR 004): `onChange` meldet beim Tippen schon jeden Zwischenstand der Jahreszahl
   * („0002“ … „2026“) als gültiges Datum. Ein unvollständiges Datum wird nicht abgeschlossen.
   */
  onCommit?: (value: string | null) => void;
  readOnly?: boolean;
  /** Breite des Side-Labels (nur bei variant='side-label') */
  labelWidth?: number;
  inputWidth?: number | string;
  /** MUI-Größe (Standard: 'small') */
  size?: "small" | "medium";
  /** Anzeigeformat (Standard: 'dd.MM.yyyy') */
  dateFormat?: string;
  /** Layout-Variante: 'side-label' (Standard) oder 'floating' (MUI Floating-Label) */
  variant?: Variant;
  /** Fehlermeldung */
  error?: string;
  /** Zusätzliches sx für das TextField */
  sx?: object;
  fullWidth?: boolean;
  /** Pflichtfeld-Markierung (Sternchen am Label) */
  required?: boolean;
  /** Erklärung beim Überfahren des Labels (nur bei variant='floating'), wie bei `InputField` */
  tooltip?: React.ReactNode;
  /**
   * Hintergrund hinter dem hochgewanderten Label bei variant='floating' – maskiert den
   * Umrandungsstrich, der sonst hinter dem Label durchscheint. Wie bei `InputField`.
   */
  labelBackgroundColor?: string;
}

// Border-Radius, Box-Shadow und Innenabstand kommen komplett aus dem Theme-Override für
// MuiPickersOutlinedInput (theme.ts) – ein eigener Innenabstand hier würde die Feldhöhe
// gegenüber den Text-/Auswahlfeldern derselben Zeile verschieben.
const textFieldSx = {};

const readOnlyFieldSx = {
  "& .MuiPickersOutlinedInput-root": {
    boxShadow: "none",
    padding: 0,
    "& .MuiPickersOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
};

const parseDateString = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const parsed = parse(dateStr.slice(0, 10), "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : null;
};

/**
 * Wiederverwendbares Datumsfeld mit DatePicker.
 *
 * variant='side-label' (Standard): Label links neben dem Feld
 * variant='floating': MUI Floating-Label im Outlined-Stil
 */
const InputFieldDate: React.FC<InputFieldDateProps> = ({
  label,
  value,
  onChange,
  onCommit,
  readOnly = false,
  labelWidth = 200,
  inputWidth,
  size = "small",
  dateFormat = "dd.MM.yyyy",
  variant = "side-label",
  error,
  sx: sxOverride = {},
  fullWidth = true,
  required = false,
  tooltip,
  labelBackgroundColor = colors.background.paper,
}) => {
  const editable = !readOnly;

  // Was beim nächsten Abschluss gemeldet wird: undefined, solange sich nichts geändert hat
  // oder das Datum unvollständig ist.
  const entwurf = useRef<string | null | undefined>(undefined);

  const handleChange = (newValue: Date | null) => {
    if (newValue && isValid(newValue)) {
      const wert = format(newValue, "yyyy-MM-dd");
      entwurf.current = wert;
      onChange(wert);
    } else {
      entwurf.current = newValue === null ? null : undefined;
      onChange(null);
    }
  };

  const abschliessen = () => {
    if (!onCommit || entwurf.current === undefined) return;
    const wert = entwurf.current;
    entwurf.current = undefined;
    onCommit(wert);
  };

  const baseSx = editable ? textFieldSx : readOnlyFieldSx;

  if (variant === "floating") {
    return (
      <DatePicker
        label={
          tooltip ? (
            <InfoTooltip title={tooltip} placement="top">
              <span>{label}</span>
            </InfoTooltip>
          ) : (
            label
          )
        }
        value={parseDateString(value)}
        onChange={handleChange}
        onAccept={abschliessen}
        disabled={readOnly}
        format={dateFormat}
        slotProps={{
          textField: {
            fullWidth,
            size,
            required,
            onBlur: abschliessen,
            error: !!error,
            helperText: error,
            sx: { ...floatingLabelMaskSx(labelBackgroundColor), ...sxOverride },
          },
          openPickerButton: {
            sx: editable
              ? { "& .MuiSvgIcon-root": { fontSize: 20 } }
              : { display: "none" },
          },
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: error ? "flex-start" : "center",
        gap: 2,
      }}
    >
      {label && (
        <Typography
          sx={{
            width: labelWidth,
            minWidth: labelWidth,
            whiteSpace: "nowrap",
            pt: error ? "10px" : 0,
          }}
        >
          {label}
        </Typography>
      )}
      <Box sx={{ flex: 1, maxWidth: inputWidth, minWidth: 0 }}>
        <DatePicker
          value={parseDateString(value)}
          onChange={handleChange}
          onAccept={abschliessen}
          disabled={readOnly}
          format={dateFormat}
          slotProps={{
            textField: {
              fullWidth: true,
              size,
              onBlur: abschliessen,
              error: !!error,
              helperText: error,
              sx: { ...baseSx, ...sxOverride },
            },
            openPickerButton: {
              sx: editable
                ? { "& .MuiSvgIcon-root": { fontSize: 20 } }
                : { display: "none" },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default InputFieldDate;
